<template>
  <div id="team_selection">
    <v-row>
      <v-col>
        <h3>{{ $tc("memberRegistration.step_team_selection.content.title") }}</h3>
        <p>{{ $tc("memberRegistration.step_team_selection.content.description") }}</p>
      </v-col>
    </v-row>

    <v-row v-if="loading" justify="center">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate :color="buttonColor"></v-progress-circular>
      </v-col>
    </v-row>

    <template v-else-if="teams.length === 0">
      <v-row>
        <v-col>
          <v-alert type="info" outlined>
            {{ $tc("memberRegistration.step_team_selection.content.no_teams") }}
          </v-alert>
        </v-col>
      </v-row>
    </template>

    <template v-else>
      <div v-for="(participant, pIndex) in selectedParticipants" :key="participant.userId">
        <v-row v-if="selectedParticipants.length > 1" class="mb-1">
          <v-col>
            <h4>{{ participant.fName }} {{ participant.lName }}</h4>
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-for="team in teams"
            :key="team.id + '-' + pIndex"
            cols="12"
            sm="6"
            md="4"
          >
            <v-card
              outlined
              :class="['team-card', { 'team-card--selected': teamSelections[participant.userId] === team.id }]"
              @click="selectTeam(pIndex, team.id)"
            >
              <v-card-text class="text-center">
                <span class="team-name">
                  <v-icon
                    v-if="teamSelections[participant.userId] === team.id"
                    color="green"
                    small
                    class="mr-1"
                  >mdi-check-circle</v-icon>
                  {{ team.name }}
                </span>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-divider v-if="pIndex < selectedParticipants.length - 1" class="my-4"></v-divider>
      </div>
    </template>

    <v-row class="mt-4">
      <v-col md="6" sm="6" cols="6" class="text-left">
        <v-btn :color="buttonColor" class="btn_next" @click="setStep(prevStep)">
          {{ $tc("registerEvent.prev") }}
        </v-btn>
      </v-col>
      <v-col md="6" sm="6" cols="6" class="text-right">
        <v-btn :color="buttonColor" class="btn_next" :disabled="!allTeamsSelected" @click="setStep(nextStep)">
          {{ $tc("registerEvent.next") }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import APIService from "@/services/APIService";
import { VRow, VCol, VBtn, VCard, VCardText, VIcon, VDivider, VAlert, VProgressCircular } from "vuetify/lib";

export default {
  components: {
    VRow,
    VCol,
    VBtn,
    VCard,
    VCardText,
    VIcon,
    VDivider,
    VAlert,
    VProgressCircular,
  },
  props: {
    step: Number,
    buttonColor: String,
    previousStep: Number,
    nextStepValue: {
      type: Number,
      default: 4,
    },
    organizationId: [Number, String],
    selectedParticipants: Array,
  },
  data() {
    return {
      teams: [],
      loading: false,
      teamsLoaded: false,
      teamSelections: {},
    };
  },
  computed: {
    allTeamsSelected() {
      if (!this.selectedParticipants || this.selectedParticipants.length === 0) return false;
      return this.selectedParticipants.every(
        (p) => this.teamSelections[p.userId]
      );
    },
    prevStep() {
      return this.previousStep;
    },
    nextStep() {
      return this.nextStepValue;
    },
  },
  watch: {
    step(val) {
      if (val === 3.2 && !this.teamsLoaded) {
        this.fetchTeams();
      }
    },
  },
  methods: {
    setStep(step) {
      this.$emit("nextStep", step);
    },
    selectTeam(participantIndex, teamId) {
      const participant = this.selectedParticipants[participantIndex];
      const currentVal = this.teamSelections[participant.userId];
      const newValue = currentVal === teamId ? null : teamId;
      this.$set(this.teamSelections, participant.userId, newValue);
      this.$set(participant, "organizationTeamId", newValue);
    },
    async fetchTeams() {
      this.loading = true;
      try {
        const resp = await APIService.fetch({
          path: `${this.$store.getters.env.get("VUE_APP_ROOT")}/laravel/public/api/v1/participant/organization/${this.organizationId}/teams`,
        });
        if (resp.data.success) {
          this.teams = resp.data.message;
          this.teamsLoaded = true;
        }
      } catch (e) {
        // silenced
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.team-card {
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.team-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
.team-card--selected {
  border: 2px solid #4caf50;
  background-color: #e8f5e9;
}
.team-name {
  font-weight: 600;
  font-size: 0.95rem;
}
</style>
